import throttle from 'lodash.throttle'
import { useEffect, useState } from 'react'

interface UseIsScrolledProps {
  target?: React.RefObject<HTMLElement | null>
}

export const useIsScrolled = ({ target }: UseIsScrolledProps = {}): boolean => {
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    if (target && !target.current) {
      return
    }

    const scrollElement: HTMLElement | Window = target?.current ?? window
    const getScrollTop = (): number => {
      if (scrollElement === window) {
        return window.scrollY ?? document.documentElement?.scrollTop ?? 0
      }
      return (scrollElement as HTMLElement).scrollTop ?? 0
    }

    const handleScroll = throttle(() => {
      setIsScrolled(getScrollTop() > 0)
    }, 100)

    // Defer the initial scroll read to the next frame so reading scrollTop
    // doesn't force a synchronous reflow during the mount commit.
    const rafId = requestAnimationFrame(handleScroll)
    scrollElement.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      cancelAnimationFrame(rafId)
      scrollElement.removeEventListener('scroll', handleScroll)
      handleScroll.cancel?.()
    }
  }, [target])

  return isScrolled
}
